import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SchoolIcon from '@mui/icons-material/School';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import trainers from '../data/trainers';
import ButtonComponent from '../components/Button';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '40vh',
  minHeight: '300px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 1s ease-in-out',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const Trainers = () => {
  const [currentBanner, setCurrentBanner] = useState(0);

  const banners = [
    '/banner1.jpg',
    '/banner2.jpg',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const trainer = trainers[0];
  const courseTitles = {
    1: 'Basic Module',
    2: 'Advanced Module',
    3: 'Pharmacovigilance',
    4: 'Advanced Pharmacovigilance',
    5: 'Clinical Data Management',
    6: 'Regulatory Affairs',
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Thrana Trainers',
    publisher: {
      '@type': 'Organization',
      name: 'Thranatech.com',
      url: 'https://www.thranatech.com',
    },
    itemListElement: trainers.map((person, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Person',
        name: person.name,
        jobTitle: person.title,
        worksFor: {
          '@type': 'Organization',
          name: 'Thranatech.com',
          url: 'https://www.thranatech.com',
        },
      },
    })),
  };

  return (
    <>
      <SEO
        title="Thrana Trainers | Pharmacovigilance & Safety Experts"
        description="Meet Thrana's lead trainers, including Vasanthi S.E and Sunil Pandita, who bring decades of experience in pharmacovigilance, quality, and safety operations."
        path="/trainers"
        structuredData={structuredData}
      />
      <HeroSection>
        {banners.map((banner, index) => (
          <BannerImage
            key={index}
            sx={{
              backgroundImage: `url(${banner})`,
              opacity: currentBanner === index ? 1 : 0,
            }}
          />
        ))}
        <Overlay>
          <Container maxWidth="md">
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              Our Lead Trainers
            </Typography>
            <Typography
              variant="h6"
              paragraph
              sx={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                color: 'white',
              }}
            >
              Industry-recognized experts delivering practical, real-world pharmacovigilance training
            </Typography>
          </Container>
        </Overlay>
      </HeroSection>
      <Box sx={{ bgcolor: '#faf8ff', minHeight: '100vh', py: 8 }}>
        <Container maxWidth="lg">

        <Card sx={{ mt: 6, overflow: 'hidden' }}>
          <Grid container>
            <Grid item xs={12}>
              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', fontWeight: 700 }}>
                  {trainer.name}
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {trainer.title}
                </Typography>
                <Typography variant="body1" paragraph>
                  {trainer.bio}
                </Typography>

                <Grid container spacing={2} sx={{ my: 2 }}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f3ecff' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Experience
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#3c005a', mt: 1 }}>
                        {trainer.experience}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f3ecff' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Courses Led
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#3c005a', mt: 1 }}>
                        {trainer.courses.length}+ Programs
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f3ecff' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Cohorts Trained
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#3c005a', mt: 1 }}>
                        1,500+ Professionals
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
                  Key Expertise
                </Typography>
                <Grid container spacing={1} sx={{ my: 1 }}>
                  {trainer.expertise.map((item) => (
                    <Grid item key={item}>
                      <Chip label={item} color="primary" variant="outlined" />
                    </Grid>
                  ))}
                </Grid>

                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
                  Certifications
                </Typography>
                <Grid container spacing={2}>
                  {trainer.certifications.map((cert) => (
                    <Grid item xs={12} md={4} key={cert}>
                      <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <SchoolIcon sx={{ color: '#3c005a' }} />
                        <Typography variant="body2">{cert}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
              <CardActions sx={{ px: { xs: 3, md: 5 }, pb: 4, gap: 2, flexWrap: 'wrap' }}>
                <ButtonComponent 
                  component="a" 
                  href={`mailto:${trainer.email}`}
                  sx={{ textDecoration: 'none' }}
                >
                  <EmailIcon sx={{ mr: 1 }} />
                  Contact Trainer
                </ButtonComponent>
                <Button
                  component="a"
                  href={trainer.linkedin || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={<LinkedInIcon />}
                  sx={{ 
                    borderColor: '#3c005a', 
                    color: '#3c005a',
                    textDecoration: 'none',
                    '&:hover': {
                      borderColor: '#5c0080',
                      backgroundColor: 'rgba(60, 0, 90, 0.04)',
                    }
                  }}
                >
                  LinkedIn
                </Button>
              </CardActions>
            </Grid>
          </Grid>
        </Card>

        <Grid container spacing={4} sx={{ mt: 6 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WorkspacePremiumIcon sx={{ color: '#3c005a' }} />
                <Typography variant="h6">Courses Led</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                The following Thrana training programs are currently led by {trainer.name}.
              </Typography>
              <Grid container spacing={1}>
                {trainer.courses.map((courseId) => (
                  <Grid item xs={12} key={courseId}>
                    <Paper sx={{ p: 2, bgcolor: '#f8f5fb' }}>
                      <Typography variant="subtitle2">
                        {courseTitles[courseId] || `Course #${courseId}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Visit the course page for complete curriculum and schedule.
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EmojiEventsIcon sx={{ color: '#3c005a' }} />
                <Typography variant="h6">Highlights & Achievements</Typography>
              </Box>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {trainer.achievements.map((item) => (
                  <li key={item} style={{ marginBottom: 12 }}>
                    <Typography variant="body1">{item}</Typography>
                  </li>
                ))}
              </ul>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 6 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
            Book a Strategy Session with {trainer.name}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Discuss custom corporate training, curriculum tailoring, or mentorship engagements.
          </Typography>
          <ButtonComponent 
            component={Link}
            to="/contact"
            sx={{ textDecoration: 'none' }}
          >
            Schedule a Call
          </ButtonComponent>
        </Box>
        </Container>
      </Box>
    </>
  );
};

export default Trainers;

